import React, { useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useIsMobile } from "../../App";

const FAQScreen = () => {
  const { theme } = useTheme();
  const { t } = useLocalization();
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [hoveredQuestion, setHoveredQuestion] = useState(null);
  const [isBackToTopHovered, setIsBackToTopHovered] = useState(false);
  const isMobile = useIsMobile();

  const faqItems = [
    {
      id: "faq1",
      question: t("faq1"),
      answer: t("faq1Answer"),
      category: "general",
    },
    {
      id: "faq2",
      question: t("faq2"),
      answer: t("faq2Answer"),
      category: "general",
    },
    {
      id: "faq3",
      question: t("faq3"),
      answer: t("faq3Answer"),
      category: "privacy",
    },
    {
      id: "faq4",
      question: t("faq4"),
      answer: t("faq4Answer"),
      category: "usage",
    },
  ];

  const toggleQuestion = (questionId) => {
    setExpandedQuestion(expandedQuestion === questionId ? null : questionId);
  };

  const getQuestionButtonStyle = (questionId) => ({
    width: "100%",
    padding: "16px 20px",
    backgroundColor:
      hoveredQuestion === questionId
        ? theme.isDark
          ? "#2C2C2E"
          : "#F8F9FA"
        : theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: "8px",
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.3s ease",
    color: theme.text,
    boxShadow:
      hoveredQuestion === questionId
        ? `0 4px 8px ${theme.shadow}`
        : `0 2px 4px ${theme.shadow}`,
    transform:
      hoveredQuestion === questionId ? "translateY(-1px)" : "translateY(0)",
  });

  const getBackToTopStyle = () => ({
    position: "fixed",
    bottom: "20px",
    right: "20px",
    padding: "12px 20px",
    backgroundColor: isBackToTopHovered ? theme.secondary : theme.primary,
    color: "#FFFFFF",
    border: "none",
    borderRadius: "25px",
    cursor: "pointer",
    boxShadow: isBackToTopHovered
      ? `0 4px 12px ${theme.shadow}`
      : `0 2px 8px ${theme.shadow}`,
    transition: "all 0.3s ease",
    fontSize: "14px",
    fontWeight: "500",
    transform: isBackToTopHovered ? "translateY(-2px)" : "translateY(0)",
  });

  const styles = {
    container: {
      maxWidth: isMobile ? "100vw" : 800,
      margin: isMobile ? "0" : "40px auto",
      padding: isMobile ? "12px 4px" : "24px",
      background: theme.surface,
      borderRadius: isMobile ? 8 : 16,
      boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
      color: theme.text,
      height: "100vh",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
    },
    header: {
      textAlign: "center",
      marginBottom: "40px",
    },
    title: {
      fontSize: isMobile ? "1.2rem" : "2rem",
      fontWeight: 600,
      marginBottom: isMobile ? 12 : 24,
      textAlign: "center",
    },
    subtitle: {
      fontSize: "1.1rem",
      color: theme.textSecondary,
      marginBottom: "30px",
    },
    faqList: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    questionText: {
      fontSize: "1.1rem",
      fontWeight: "500",
      margin: 0,
      color: theme.text,
    },
    expandIcon: {
      fontSize: "1.2rem",
      transition: "transform 0.3s ease",
      color: theme.textSecondary,
    },
    answer: {
      padding: "20px",
      backgroundColor: theme.surface,
      borderRadius: "8px",
      marginTop: "8px",
      color: theme.textSecondary,
      fontSize: "1rem",
      lineHeight: "1.6",
      boxShadow: `0 2px 4px ${theme.shadow}`,
      border: `1px solid ${theme.border}`,
    },
    category: {
      fontSize: "0.9rem",
      color: theme.textSecondary,
      marginTop: "8px",
      textTransform: "capitalize",
      padding: "4px 8px",
      backgroundColor: theme.isDark
        ? "rgba(0, 122, 255, 0.2)"
        : "rgba(0, 122, 255, 0.1)",
      borderRadius: "4px",
      display: "inline-block",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t("faqScreen")}</h1>
        <p style={styles.subtitle}>{t("faqSubtitle")}</p>
      </div>

      {/* Scrollable FAQ list area */}
      <div
        style={{
          ...styles.faqList,
          flex: 1,
          minHeight: 0,
        }}
      >
        {faqItems.map((item) => (
          <div key={item.id}>
            <button
              style={getQuestionButtonStyle(item.id)}
              onClick={() => toggleQuestion(item.id)}
              aria-expanded={expandedQuestion === item.id}
              onMouseEnter={() => setHoveredQuestion(item.id)}
              onMouseLeave={() => setHoveredQuestion(null)}
            >
              <span style={styles.questionText}>{item.question}</span>
              <span
                style={{
                  ...styles.expandIcon,
                  transform:
                    expandedQuestion === item.id
                      ? "rotate(180deg)"
                      : "rotate(0)",
                }}
              >
                ▼
              </span>
            </button>
            {expandedQuestion === item.id && (
              <div style={styles.answer}>
                <p>{item.answer}</p>
                <div style={styles.category}>
                  {t(
                    `faq${item.category.charAt(0).toUpperCase() + item.category.slice(1)}`,
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQScreen;
